import { useEffect, useState } from 'react';
import validator from 'validator';
import { Typography } from '../typography';
import { Button } from '../button';
import { Input, InputContainer } from '../input';
import { Select } from '../select';
import {
  ModalBase,
  ModalWrapper,
  ModalFooterButtons,
  ModalBodyCustomColumns,
} from './ModalComponents';
import { useStore } from '../../context';
import { containerValidator, showToRoles } from '../../utils';
import { useEditContainerMutation } from '../../hooks';
import {
  containerConditionOptions,
  containerTypes,
  INPUT_ERRORS,
  MESSAGES,
  REGEX,
} from '../../const';
import { enqueueSnackbar } from '../snackbar';
import { i18n } from '../../i18n';

export const containerEditModal: any = {};

export const ContainerEditModal = () => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const { mutate } = useEditContainerMutation();
  const [open, setOpen] = useState(false);
  const [containerNumber, setContainerNumber] = useState('');
  const [containerType, setContainerType] = useState('');
  const [condition, setCondition] = useState('');
  const [licensePlateNumber, setLicensePlateNumber] = useState('');

  const [, setUpdate] = useState<any>();

  const validators = {
    containerNumber: containerNumber
      ? !containerValidator(containerNumber)
        ? INPUT_ERRORS.NOT_VALID.CONTAINER_NUMBER
        : ''
      : true,
    licensePlateNumber:
      licensePlateNumber && licensePlateNumber !== 'TRANSFERRED' && licensePlateNumber !== 'MARKET'
        ? !validator.isLength(licensePlateNumber, { min: 4, max: 10 })
          ? INPUT_ERRORS.NOT_VALID.LICENSE_PLATE_NUMBER_LENGTH
          : !REGEX.LICENSE_PLATE_NUMBER.test(licensePlateNumber)
          ? INPUT_ERRORS.NOT_VALID.LICENSE_PLATE_NUMBER
          : ''
        : '',
  };

  useEffect(() => {
    containerEditModal.open = () => {
      setContainerNumber(containerEditModal.number);
      setContainerType(containerEditModal.type);
      setCondition(containerEditModal.condition || '');
      setLicensePlateNumber(containerEditModal.licensePlateNumber);

      containerEditModal.isSubmitClicked = false;
      setOpen(true);
    };
  }, []);

  const handleOnClose = () => {
    Object.keys(containerEditModal).forEach((key) => {
      if (key !== 'open') delete containerEditModal[key];
    });
    setOpen(!open);
  };

  const handleSubmitClick = async () => {
    containerEditModal.isSubmitClicked = true;
    setUpdate(Math.random());

    if ([...Object.values(validators).map((val) => !val)].some((val) => !val)) {
      return;
    }

    containerEditModal.isSubmitClicked = false;

    const mutateData: any = {
      id: containerEditModal.id,
      number: containerNumber,
      type: containerType,
      condition,
      licensePlateNumber: licensePlateNumber,
    };
    if (containerNumber === containerEditModal.number) delete mutateData.number;
    if (containerType === containerEditModal.type) delete mutateData.type;
    if (condition === containerEditModal.condition || !condition) delete mutateData.condition;
    if (licensePlateNumber === containerEditModal.licensePlateNumber)
      delete mutateData.licensePlateNumber;

    mutate(mutateData, {
      onSuccess: () => {
        setOpen(false);
        containerEditModal.submitClick();
      },
      onError: () => {
        enqueueSnackbar(MESSAGES.CONTAINERS.ERROR.NOT_EDITED, 'error');
      },
    });
  };

  return (
    <ModalBase id={'container-edit-modal'} open={open} onClose={handleOnClose}>
      <ModalWrapper>
        <Typography variant={'textL'} weight={'bold'}>
          {i18n.t('pages.modal.containerEditTitle', { number: containerEditModal.number })}
        </Typography>
        <ModalBodyCustomColumns columns={'1fr 1fr'}>
          {showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_MANAGER'], user) &&
            !containerEditModal.isSale && (
              <InputContainer
                id={'container-edit'}
                label={i18n.t('label.containerNumber') + '*'}
                data-size={'medium'}
                value={containerNumber}
                onChange={(e) => setContainerNumber(e.target.value)}
                error={
                  (containerEditModal.isSubmitClicked && !containerNumber) ||
                  (!!containerNumber && validators.containerNumber)
                }
                helperText={
                  containerEditModal.isSubmitClicked && !containerNumber
                    ? INPUT_ERRORS.REQUIRED.CONTAINER_NUMBER
                    : validators.containerNumber
                }
              />
            )}
          {!containerEditModal.isSale && (
            <Select
              label={i18n.t('label.containerType') + '*'}
              options={containerTypes}
              value={containerType}
              onChange={(e) => {
                setContainerType(e.target.value);
              }}
            />
          )}
        </ModalBodyCustomColumns>
        <Select
          label={i18n.t('label.condition')}
          options={containerConditionOptions}
          value={condition}
          onChange={(e) => {
            setCondition(e.target.value);
          }}
        />
        {showToRoles(['SUPER_ADMIN'], user) && !containerEditModal.isSale && (
          <Input
            label={i18n.t('label.licensePlateNumber')}
            data-size={'medium'}
            defaultValue={licensePlateNumber}
            onChange={(e) => setLicensePlateNumber(e.target.value)}
            error={!!validators.licensePlateNumber}
            helperText={validators.licensePlateNumber}
          />
        )}
        <ModalFooterButtons>
          <Button size={'medium'} data-color={'white'} onClick={handleOnClose}>
            {i18n.t('button.cancel')}
          </Button>
          <Button
            size={'medium'}
            data-color={'primary'}
            onClick={handleSubmitClick}
            disabled={
              containerNumber === containerEditModal.number &&
              containerType === containerEditModal.type &&
              licensePlateNumber === containerEditModal.licensePlateNumber &&
              condition === containerEditModal.condition
            }
          >
            {i18n.t('button.edit')}
          </Button>
        </ModalFooterButtons>
      </ModalWrapper>
    </ModalBase>
  );
};
